import { Flex, Text } from '@chakra-ui/layout';
import { useEffect, useMemo, useState } from 'react';
import { Crucible, ERC20Token } from 'types';
import { useTransactions } from 'store/transactions/useTransactions';
import { useSelectedAssets } from 'store/selectedAssets';
import { useTokenBlacklist } from 'hooks/query/useTokenBlacklist';
import AssetsLoadingSkeleton from 'components/crucible/detail/assetManagement/AssetsLoadingSkeleton';
import AssetSelector from './assetSelector';
import transferErc20 from './transferErc20';
import TokenTransferForm from './tokenTransferForm';
import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu';
import { Button } from '@chakra-ui/button';
import { FaCaretDown } from 'react-icons/fa';
import { truncate } from 'utils/address';
import { useCrucibles } from 'hooks/query/useCrucibles';
import { useSelectedCrucible } from 'store/selectedCrucible';

type Props = {
  crucible: Crucible;
  crucibleAssets: ERC20Token[] | undefined;
  assetsStatus: 'idle' | 'error' | 'loading' | 'success';
  assetsFetching: boolean;
};

const TransferBetweenCrucibles: React.FC<Props> = ({
  crucible,
  crucibleAssets,
  assetsStatus,
  assetsFetching
}) => {
  const { transferBetweenCrucibles } = useTransactions();
  const { data: tokenBlacklist = [] } = useTokenBlacklist();
  const { data: crucibles } = useCrucibles();
  const { selectedCrucible } = useSelectedCrucible();
  const { selectedCrucibleAsset, setSelectedCrucibleAsset } = useSelectedAssets(
    crucible?.id
  );

  const [amount, setAmount] = useState('');
  const [selectedCrucibleDropDown, setSelectedCrucibleDropdown] = useState('');

  // Reset dropdown on selected crucible change to avoid transfering token to same crucible
  useEffect(() => {
    setSelectedCrucibleDropdown('');
  }, [selectedCrucible]);

  const assetSelector = useMemo(() => {
    return (
      crucibleAssets && (
        <AssetSelector
          selectedAsset={selectedCrucibleAsset}
          assets={crucibleAssets}
          setSelectedAsset={setSelectedCrucibleAsset}
          tokenBlacklist={tokenBlacklist}
        />
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrucibleAsset, crucibleAssets]);

  if (assetsStatus === 'error') {
    return (
      <Flex p={[4, 6]} borderRadius='xl' bg='gray.700'>
        <Text textAlign='center'>
          Failed to get assets in your Crucible. Please try refreshing the page.
        </Text>
      </Flex>
    );
  }

  if (
    assetsStatus === 'loading' ||
    assetsStatus === 'idle' ||
    assetsFetching ||
    !crucible ||
    !crucible.id
  ) {
    return <AssetsLoadingSkeleton />;
  }

  if (!selectedCrucibleAsset) {
    return (
      <Flex p={[4, 6]} borderRadius='xl' bg='gray.700'>
      <Text textAlign='center'>
        You have no assets in your Crucible to be transferred.
      </Text>
    </Flex>
    )
  }

  if (!crucibleAssets) {
    return (
      <Flex p={[4, 6]} borderRadius='xl' bg='gray.700' direction='column'>
        <Text textAlign='center'>
          You have no unlocked assets available to transfer from your Crucible.
          You can view a list of all of your assets in the "Summary" tab.
        </Text>
      </Flex>
    );
  }

  const tokenBalance = selectedCrucibleAsset.value.amount;
  const tokenDecimals = selectedCrucibleAsset.decimals || 18;

  const handleTransfer = () => {
    transferErc20(
      tokenBlacklist,
      selectedCrucibleAsset,
      transferBetweenCrucibles,
      crucible.id,
      amount,
      tokenDecimals,
      selectedCrucibleDropDown
    );
    setAmount('');
  };

  return (
    <TokenTransferForm
      tokenBalance={tokenBalance}
      tokenDecimals={tokenDecimals}
      selectedAsset={selectedCrucibleAsset}
      amount={amount}
      setAmount={setAmount}
      assetSelector={assetSelector}
      handleTransfer={handleTransfer}
      submitButtonText={'Transfer'}
      isDisabled={!selectedCrucibleDropDown}
    >
      {crucibles && (
        <Menu matchWidth autoSelect={false}>
          <MenuButton
            variant='ghost'
            bg='gray.600'
            as={Button}
            mt={4}
            pl={10} // pushes the text to the right as it's slightly off-centered by the rightIcon
            isFullWidth
            rightIcon={<FaCaretDown />}
            _hover={{
              bg: 'gray.600'
            }}
            _active={{
              bg: 'gray.600'
            }}
          >
            {selectedCrucibleDropDown
              ? truncate(selectedCrucibleDropDown)
              : 'Select crucible'}
          </MenuButton>
          <MenuList zIndex={4}>
            {crucibles
              .filter((_crucible: Crucible) => _crucible.id !== crucible.id)
              .map((crucible: Crucible) => (
                <MenuItem
                  key={crucible.id}
                  borderRadius='sm'
                  bg={
                    crucible.id === selectedCrucibleDropDown ? 'gray.600' : ''
                  }
                  onClick={() => setSelectedCrucibleDropdown(crucible.id)}
                >
                  <Text>ID: {truncate(crucible.id)}</Text>
                </MenuItem>
              ))}
          </MenuList>
        </Menu>
      )}
    </TokenTransferForm>
  );
};

export default TransferBetweenCrucibles;
